<template>
  <div class="ais-page">
    <div class="ais-layout">
      <section class="ais-sidebar">
        <header class="ais-sidebar__header">
          <h2 class="ais-sidebar__title">
            AIS Targets
          </h2>
          <p class="ais-sidebar__description">
            AIS targets inside the current map view. Request live data after zooming in on the area of interest.
          </p>
          <div class="ais-sidebar__controls">
            <b-button
              variant="primary"
              size="sm"
              class="ais-sidebar__button"
              :disabled="!canRequestData || loading"
              @click="requestAisTargets"
            >
              <b-spinner small v-if="loading" class="mr-2" />
              <span>Show AIS</span>
              <span v-if="boundsLabel" class="ais-sidebar__button-bounds">({{ boundsLabel }})</span>
            </b-button>
            <small v-if="!canRequestData" class="ais-sidebar__hint">
              Zoom in to at least level {{ zoomThreshold }} to enable data requests (current: {{ currentZoomLabel }}).
            </small>
            <small v-else-if="lastFetchedLabel" class="ais-sidebar__hint">
              Last fetch: {{ lastFetchedLabel }}
            </small>
          </div>
        </header>
        <ais-table
          ref="table"
          :targets="aisTargets"
          :selected-id="selectedTargetId"
          :loading="loading"
          :error="errorMessage"
          @select="handleTableSelect"
        />
      </section>
      <section class="ais-map">
        <div class="ais-map__header">
          <div class="ship-badge" :class="{ 'ship-badge--clickable': hasSelectedShip }" @click="centerMapOnShip">
            <span>{{ shipNameDisplay }}</span>
          </div>
        </div>
        <ais-map
          ref="aisMap"
          class="ais-map__canvas"
          :targets="aisTargets"
          :selected-id="selectedTargetId"
          :ownship="ownShipPosition"
          :route="routeCoordinates"
          @target-click="handleMapTargetClick"
          @bounds-change="handleBoundsChange"
        />
      </section>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapStores } from 'pinia'
import { useRoutesStore } from '@/stores/routes'
import { useDeviceStore } from '@/stores/device'
import { useFleetStore } from '@/stores/fleet'
import { elBackAxios } from '@/services/myAxios'
import AISTable from './AISTable.vue'
import AISMap from './AISMap.vue'

const ZOOM_THRESHOLD = 6

export default {
  name: 'AISDashboard',
  components: {
    AisTable: AISTable,
    AisMap: AISMap,
  },
  data() {
    return {
      aisTargets: [],
      loading: false,
      errorMessage: '',
      selectedTargetId: null,
      lastBounds: null,
      currentZoom: null,
      lastFetchedAt: null,
    }
  },
  computed: {
    ...mapStores(useRoutesStore, useDeviceStore, useFleetStore),
    canRequestData() {
      return this.lastBounds !== null && this.currentZoom >= this.zoomThreshold
    },
    zoomThreshold() {
      return ZOOM_THRESHOLD
    },
    currentZoomLabel() {
      return this.currentZoom === null ? 'n/a' : this.currentZoom.toFixed(1)
    },
    shipNameDisplay() {
      return this.deviceStore.getShipName || this.routesStore.getRouteName || 'N/A'
    },
    hasSelectedShip() {
      return Boolean(this.ownShipPosition)
    },
    routeCoordinates() {
      const waypoints = this.routesStore.getShipWaypoints
      if (!Array.isArray(waypoints)) {
        return []
      }
      return waypoints
        .map(point => {
          const lat = Number(point.lat ?? point.latitude)
          const lon = Number(point.lon ?? point.longitude)
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null
          }
          return { lat, lon }
        })
        .filter(Boolean)
    },
    ownShipPosition() {
      const pos = this.fleetStore.selectedShipPosition
      if (pos && Number.isFinite(Number(pos.lat)) && Number.isFinite(Number(pos.lon))) {
        return {
          lat: Number(pos.lat),
          lon: Number(pos.lon),
          cog: Number(pos.cog) || 0,
          sog: Number(pos.sog) || 0,
        }
      }
      const fallback = this.routeCoordinates[0]
      if (fallback) {
        return { lat: fallback.lat, lon: fallback.lon, cog: 0, sog: 0 }
      }
      return null
    },
    boundsLabel() {
      if (!this.lastBounds) {
        return ''
      }
      const { north, south, east, west } = this.lastBounds
      return `${south.toFixed(3)} – ${north.toFixed(3)}, ${west.toFixed(3)} – ${east.toFixed(3)}`
    },
    lastFetchedLabel() {
      if (!this.lastFetchedAt) {
        return ''
      }
      const ts = dayjs(this.lastFetchedAt)
      if (!ts.isValid()) {
        return this.lastFetchedAt
      }
      return ts.local().format('YYYY-MM-DD HH:mm:ss')
    },
  },
  methods: {
    handleTableSelect(target) {
      if (!target) {
        return
      }
      this.selectedTargetId = target.id
      this.$refs.aisMap?.focusOnTarget(target, { pan: true, openPopup: true })
    },
    handleMapTargetClick(target) {
      if (!target) {
        return
      }
      this.selectedTargetId = target.id
      this.$refs.table?.scrollToTarget(target.id)
    },
    handleBoundsChange(payload) {
      if (!payload) {
        return
      }
      this.lastBounds = payload.bounds || null
      this.currentZoom = payload.zoom ?? null
    },
    async requestAisTargets() {
      if (!this.canRequestData || this.loading) {
        return
      }
      await this.fetchAisTargets()
    },
    async fetchAisTargets() {
      if (!this.lastBounds) {
        this.errorMessage = 'Map bounds are not available yet.'
        return
      }
      this.loading = true
      this.errorMessage = ''

      try {
        // Use elBackAxios instead of direct fetch
        const response = await elBackAxios.get('api/ais/zone', {
          params: {
            lat1: this.lastBounds.north.toFixed(5),
            lon1: this.lastBounds.west.toFixed(5),
            lat2: this.lastBounds.south.toFixed(5),
            lon2: this.lastBounds.east.toFixed(5),
            response: 'simple',
          }
        })
        const payload = response.data
        const collection = Array.isArray(payload?.data?.data) ? payload.data.data : []

        const targets = collection
          .map(item => {
            const lat = Number(item.lat)
            const lon = Number(item.lng)
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
              return null
            }
            const idSource = item.mmsi ?? `${lat}-${lon}-${item.vessel_name ?? 'target'}`
            const speedValue = Number(item.speed)
            const courseValue = Number(item.course)
            return {
              id: String(idSource),
              name: item.vessel_name || 'Unknown vessel',
              mmsi: item.mmsi ?? null,
              imo: item.imo ?? null,
              type: item.vtype ?? null,
              lat,
              lon,
              speed: Number.isFinite(speedValue) ? speedValue : null,
              course: Number.isFinite(courseValue) ? this.normalizeCourse(courseValue) : null,
              navStatus: item.nav_status ?? null,
              received: item.received ?? null,
              source: payload?.source || 'live',
            }
          })
          .filter(Boolean)

        // Сортируем цели по времени получения, чтобы свежие показывались первыми
        targets.sort((a, b) => {
          const ta = Date.parse(a.received || '') || 0
          const tb = Date.parse(b.received || '') || 0
          return tb - ta
        })

        this.aisTargets = targets
        this.lastFetchedAt = payload?.fetched_at || new Date().toISOString()

        if (targets.length) {
          this.selectedTargetId = targets[0].id
          this.$nextTick(() => {
            this.$refs.table?.scrollToTarget(targets[0].id)
          })
        } else {
          this.selectedTargetId = null
        }
      } catch (error) {
        console.error('AIS fetch error:', error)
        this.errorMessage = error?.message || 'Failed to load AIS data.'
      } finally {
        this.loading = false
      }
    },
    normalizeCourse(value) {
      if (!Number.isFinite(value)) {
        return value
      }
      const normalized = value % 360
      if (normalized < 0) {
        return normalized + 360
      }
      return normalized
    },
    centerMapOnShip() {
      if (!this.ownShipPosition) {
        return
      }
      const target = {
        lat: this.ownShipPosition.lat,
        lon: this.ownShipPosition.lon,
        name: this.shipNameDisplay,
      }
      this.$refs.aisMap?.focusOnTarget(target, { pan: true })
    },
  },
}
</script>

<style scoped>
@import '@/assets/styles/components/buttons.css';

.ais-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.ais-layout {
  --ais-sidebar-width: 664px;
  display: flex;
  flex-direction: row;
  height: calc(100vh - 110px);
  min-height: 520px;
  background: #f8f9fb;
  border-radius: 12px;
  overflow: hidden;
}

.ais-sidebar {
  width: var(--ais-sidebar-width);
  max-width: var(--ais-sidebar-width);
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ais-sidebar__header {
  padding: 18px 20px 12px;
  border-bottom: 1px solid #e6eef9;
}

.ais-sidebar__title {
  font-size: 1.25rem;
  margin-bottom: 6px;
  color: #172554;
}

.ais-sidebar__description {
  margin-bottom: 12px;
  color: #4a5568;
  font-size: 0.9rem;
}

.ais-sidebar__controls {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ais-sidebar__hint {
  color: #64748b;
  font-size: 0.8rem;
}

.ais-sidebar__button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ais-sidebar__button-bounds {
  color: inherit;
  font-size: 0.74rem;
}

.ais-map {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
}

.ais-map__header {
  display: flex;
  align-items: center;
  padding: 8px 20px 6px;
  background: #ffffff;
  border-bottom: 1px solid #dce3ef;
}

.ais-map__canvas {
  flex: 1;
  min-height: 360px;
}

@media (max-width: 1600px) {
  .ais-layout {
    --ais-sidebar-width: 605px;
  }
}

@media (max-width: 1400px) {
  .ais-layout {
    --ais-sidebar-width: 518px;
  }
}

@media (max-width: 1100px) {
  .ais-layout {
    flex-direction: column;
    --ais-sidebar-width: 100%;
  }

  .ais-sidebar {
    width: 100%;
    max-width: none;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }

  .ais-map__canvas {
    min-height: 320px;
  }
}
</style>
